import { useState, useCallback, useRef, useEffect } from 'react'
import { ImageTransform, DragState } from './types'
import { scaleIncrement, minScale, maxScale } from './constants'
import { calculateEmptySpace } from './calculateEmptySpace'
import { outpaintImageContent, ImageResult } from '../imageEditingApi'
import { useLoading } from '../editor/loading_state/useLoading'

interface UseImageTransformOptions {
	containerWidth: number
	containerHeight: number
	imageWidth: number
	imageHeight: number
	selectedDimension: {
		targetWidth: number
		targetHeight: number
	}
	onTransformChange?: (transform: ImageTransform) => void
	onOutpaintStart?: () => void
	onOutpaintComplete?: (result: ImageResult) => void
	onOutpaintError?: (error: Error) => void
}

export const useImageTransform = ({
	containerWidth,
	containerHeight,
	imageWidth,
	imageHeight,
	selectedDimension,
	onTransformChange,
	onOutpaintStart,
	onOutpaintComplete,
	onOutpaintError,
}: UseImageTransformOptions) => {
	// Transform state
	const [transform, setTransform] = useState<ImageTransform>({
		scale: 1,
		position: { x: 0, y: 0 },
		isDragging: false,
	})

	// Replace loading state with useLoading hook
	const { startLoading, stopLoading } = useLoading('image-transform')

	// Calculate empty space
	const emptySpace = calculateEmptySpace(
		containerWidth,
		containerHeight,
		imageWidth,
		imageHeight,
		transform.scale,
		transform.position,
	)

	// Drag state reference
	const dragState = useRef<DragState | null>(null)

	// Container ref for wheel event
	const containerRef = useRef<HTMLDivElement | null>(null)

	// Update transform and notify parent
	const updateTransform = useCallback(
		(newTransform: ImageTransform) => {
			setTransform(newTransform)
			onTransformChange?.(newTransform)
		},
		[onTransformChange],
	)

	// Handle mouse wheel for scaling
	const handleWheel = useCallback(
		(e: WheelEvent) => {
			e.preventDefault()
			const scaleChange = e.deltaY > 0 ? -scaleIncrement : scaleIncrement
			const newScale = Math.max(minScale, Math.min(maxScale, transform.scale + scaleChange))

			// Calculate container center
			const containerCenterX = containerWidth / 2
			const containerCenterY = containerHeight / 2

			// Calculate current image center relative to container
			const currentImageCenterX = containerCenterX + transform.position.x
			const currentImageCenterY = containerCenterY + transform.position.y

			// Calculate how much the position needs to change to keep the center point stable
			const newPositionX =
				containerCenterX - (containerCenterX - currentImageCenterX) * (newScale / transform.scale)
			const newPositionY =
				containerCenterY - (containerCenterY - currentImageCenterY) * (newScale / transform.scale)

			updateTransform({
				scale: newScale,
				position: {
					x: newPositionX - containerCenterX,
					y: newPositionY - containerCenterY,
				},
				isDragging: false,
			})
		},
		[transform, containerWidth, containerHeight, updateTransform],
	)

	// Set up wheel event listener
	useEffect(() => {
		const element = containerRef.current
		if (!element) return

		const handleWheelEvent = (e: WheelEvent) => {
			e.preventDefault()
			handleWheel(e)
		}

		element.addEventListener('wheel', handleWheelEvent, { passive: false })
		return () => {
			element.removeEventListener('wheel', handleWheelEvent)
		}
	}, [handleWheel])

	// Handle drag start
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			dragState.current = {
				startX: e.clientX,
				startY: e.clientY,
				startPositionX: transform.position.x,
				startPositionY: transform.position.y,
			}

			updateTransform({
				...transform,
				isDragging: true,
			})
		},
		[transform, updateTransform],
	)

	// Handle drag move
	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!dragState.current || !transform.isDragging) return

			const deltaX = e.clientX - dragState.current.startX
			const deltaY = e.clientY - dragState.current.startY

			updateTransform({
				...transform,
				position: {
					x: dragState.current.startPositionX + deltaX,
					y: dragState.current.startPositionY + deltaY,
				},
			})
		},
		[transform, updateTransform],
	)

	// Handle drag end
	const handleMouseUp = useCallback(() => {
		if (!transform.isDragging) return

		dragState.current = null
		updateTransform({
			...transform,
			isDragging: false,
		})
	}, [transform, updateTransform])

	// Update outpainting function to use the loading hook
	const handleOutpaint = useCallback(
		async (imageBase64: string, prompt?: string) => {
			if (!emptySpace) {
				return
			}

			try {
				startLoading('Processing image adjustments...', 2)
				onOutpaintStart?.()

				// Get all empty space values - these already account for scaling
				const adjustedValues = {
					left: Math.round(emptySpace.originalEmptyLeft),
					right: Math.round(emptySpace.originalEmptyRight),
					up: Math.round(emptySpace.originalEmptyUp),
					down: Math.round(emptySpace.originalEmptyDown),
				}

				// Check if we need to process the image (either crop or outpaint)
				const needsProcessing = Object.values(adjustedValues).some((value) => value !== 0)

				if (!needsProcessing) {
					// No processing needed, just return the original image
					return
				}

				// Perform outpainting with the original image and let the backend handle cropping
				// We send both the padding values and the target dimensions
				const result = await outpaintImageContent({
					imageBase64,
					...adjustedValues, // Send adjusted values directly
					prompt: prompt ?? '',
					creativity: 0.5,
					output_format: 'png',
					targetWidth: selectedDimension.targetWidth,
					targetHeight: selectedDimension.targetHeight
				})

				onOutpaintComplete?.(result)

				// Reset transform after successful processing
				setTransform({
					scale: 1,
					position: { x: 0, y: 0 },
					isDragging: false,
				})
			} catch (error) {
				// Handle error
				stopLoading()
				onOutpaintError?.(error instanceof Error ? error : new Error(String(error)))
			} finally {
				stopLoading()
			}
		},
		[emptySpace, onOutpaintStart, onOutpaintComplete, onOutpaintError, startLoading, stopLoading, selectedDimension],
	)

	return {
		transform,
		containerRef,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		emptySpace,
		setTransform,
		handleOutpaint,
	}
}
